import {
  resolvePageFromPath,
  stripLocalePrefixFromPathname,
  type PageMeta,
} from '@web-studio/shared-schemas';
import { activateNavDrawerHashLink } from './navDrawerHash';

export type InterceptPreviewPageLinkOptions = {
  supportedLocales?: readonly string[];
  defaultLocale?: string;
  onLocaleChange?: (locale: string) => void;
};

/**
 * In studio view mode, page-path anchors (`/menu`, `/impressum`) must not navigate
 * the sandbox iframe. Map them to shell page switches via `onNavigateToPageSlug`.
 * Hash / mailto / external URLs are left alone.
 *
 * Homepage section links (`/#reserve`) switch to Home. Contact (`/#contact`) is
 * footer chrome on every page — scroll in place instead of jumping to Home.
 * Locale-prefixed paths (`/en`, `/fr/menu`) switch locale when `onLocaleChange` is set.
 */
export function interceptPreviewPageLinkClick(
  event: { target: EventTarget | null; preventDefault: () => void },
  pages: PageMeta[],
  onNavigateToPageSlug: (slug: string) => void,
  options?: InterceptPreviewPageLinkOptions,
): boolean {
  const target = event.target;
  if (!target || typeof (target as Element).closest !== 'function') {
    return false;
  }

  const anchor = (target as Element).closest('a[href]');
  if (!anchor || typeof anchor.getAttribute !== 'function') {
    return false;
  }

  const hrefAttr = anchor.getAttribute('href');
  if (!hrefAttr) {
    return false;
  }

  const trimmed = hrefAttr.trim();
  if (
    !trimmed ||
    trimmed === '#' ||
    trimmed.startsWith('#') ||
    trimmed.startsWith('mailto:') ||
    trimmed.startsWith('tel:') ||
    trimmed.startsWith('//') ||
    /^[a-z][a-z0-9+.-]*:/i.test(trimmed)
  ) {
    return false;
  }

  // `/#contact` or `/en#contact` — footer is global; stay on the current page.
  const contactHash = trimmed.match(/^\/(?:[a-zA-Z]{2})?#contact$/i);
  if (contactHash && typeof document !== 'undefined') {
    event.preventDefault();
    if (!activateNavDrawerHashLink(anchor)) {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    return true;
  }

  let pathname: string;
  try {
    pathname = new URL(trimmed, 'https://preview.local').pathname;
  } catch {
    return false;
  }

  const supportedLocales = options?.supportedLocales ?? [];
  const stripped =
    supportedLocales.length > 0
      ? stripLocalePrefixFromPathname(pathname, supportedLocales)
      : pathname;

  if (supportedLocales.length > 0 && options?.onLocaleChange) {
    const first = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
    if (first && supportedLocales.map((code) => code.toLowerCase()).includes(first)) {
      event.preventDefault();
      options.onLocaleChange(first);
      const page = resolvePageFromPath(stripped, pages);
      if (page) {
        onNavigateToPageSlug(page.slug);
      }
      return true;
    }
  }

  const page = resolvePageFromPath(stripped, pages);
  if (page) {
    event.preventDefault();
    onNavigateToPageSlug(page.slug);
    return true;
  }

  // Keep the sandbox SPA mounted: swallow other site-relative paths (locale
  // switcher, unknown routes) that would otherwise unload the iframe.
  if (trimmed.startsWith('/')) {
    event.preventDefault();
    return true;
  }

  return false;
}
